import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, getDocs, getDoc, Timestamp, setDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import HutCard from '../components/HutCard';
import CreateHutModal from '../components/CreateHutModal';
import LoadingSkeleton from '../components/LoadingSkeleton';
import PendingInvites from '../components/PendingInvites';
import toast from 'react-hot-toast';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

// Helper for distance
function haversineDistance(lat1, lon1, lat2, lon2) {
    function toRad(x) { return x * Math.PI / 180; }
    const R = 3958.8; // Radius of Earth in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

function HutsPage() {
    // Data State
    const [publicGroups, setPublicGroups] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const [userPti, setUserPti] = useState(null);

    // UI State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [existingGroupIds, setExistingGroupIds] = useState(new Set());

    // Filter State
    const [filters, setFilters] = useState({
        minPti: -10,
        maxPti: 100,
        showAll: false
    });

    const navigate = useNavigate();

    // Fetch My Games (User's Groups)
    const fetchUserGroups = useCallback(async (userId) => {
        try {
            const userGroupsSnapshot = await getDocs(collection(db, 'userProfiles', userId, 'groups'));
            const groupsData = [];
            const myIds = new Set();

            for (const userGroupDoc of userGroupsSnapshot.docs) {
                const userGroupData = userGroupDoc.data();
                myIds.add(userGroupData.groupId);

                const groupRef = doc(db, 'groups', userGroupData.groupId);
                const groupSnap = await getDoc(groupRef);

                if (groupSnap.exists()) {
                    const groupData = groupSnap.data();
                    groupsData.push({
                        id: userGroupData.groupId,
                        ...groupData,
                        userRole: userGroupData.role
                    });
                }
            }
            setMyGroups(groupsData);
            setExistingGroupIds(myIds);
            return myIds; // Return for use in open huts fetch
        } catch (error) {
            console.error('Error fetching user groups:', error);
            return new Set();
        }
    }, []);

    const fetchOpenHuts = useCallback(async (userId, pti, myIdsOverride = null, currentFilters = filters) => {
        setLoading(true);
        try {
            const myIds = myIdsOverride || existingGroupIds;

            // 1. Fetch User Profile for Home Club (if not already valid)
            let homeClubId = null;
            let homeClubData = null;
            if (userId) {
                const profileSnap = await getDoc(doc(db, 'userProfiles', userId));
                if (profileSnap.exists()) {
                    homeClubId = profileSnap.data().preferredClubId;
                }
            }

            // 2. Fetch All Locations (Lookup Map)
            const locationsSnapshot = await getDocs(collection(db, 'locations'));
            const locationsMap = {}; // id -> data
            const locationsByName = {}; // name -> data
            locationsSnapshot.forEach(doc => {
                const data = doc.data();
                locationsMap[doc.id] = { id: doc.id, ...data };
                locationsMap[doc.id] = { id: doc.id, ...data };
                if (data.name) locationsByName[data.name] = { id: doc.id, ...data };
                if (data.ClubName) locationsByName[data.ClubName] = { id: doc.id, ...data };
            });

            if (homeClubId && locationsMap[homeClubId]) {
                homeClubData = locationsMap[homeClubId];
            }

            // 3. Fetch all groups
            const groupsSnapshot = await getDocs(collection(db, 'groups'));
            const allGroups = groupsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // 4. Filter & Calculate Distance
            let visibleGroups = allGroups.filter(group => {
                // Exclude my groups
                if (myIds.has(group.id)) return false;

                // PTI Filter Logic
                let effectiveMin = -100;
                let effectiveMax = 100;

                /* Removed complex filter logic for now, relying on Sort */
                /* Keeping PTI logic if we want to gray out or strict filter later? */
                /* For now, let's keep the existing boolean filter if the user manually inputs it */
                if (currentFilters.minPti !== -10 || currentFilters.maxPti !== 100) {
                    if (currentFilters.minPti !== '') effectiveMin = parseFloat(currentFilters.minPti);
                    if (currentFilters.maxPti !== '') effectiveMax = parseFloat(currentFilters.maxPti);

                    const groupMin = group.minPti !== undefined ? group.minPti : -100;
                    const groupMax = group.maxPti !== undefined ? group.maxPti : 100;
                    return groupMin >= effectiveMin && groupMax <= effectiveMax;
                }
                // Default "Smart Match" based on User's PTI is NOT applied here to hide them, 
                // because we want to show everything sorted by distance. 
                // But maybe we want to keep the "Show All" toggle? 
                // User said "Simpler just to show huts by distance... maybe limit it to top 10".
                // Let's NOT filter by PTI by default anymore, unless user manually filters.

                // However, the existing code logic WAS filtering by PTI. 
                // "Show All" was needed. 
                // Let's KEEP the PTI filter if "Show All" is FALSE.
                if (currentFilters.showAll) return true;

                // Explicitly check User PTI or Fallback if no user PTI is known
                let ptiToCheck = pti;
                if (ptiToCheck === null || ptiToCheck === undefined) {
                    ptiToCheck = 50; // Fallback "average" if we can't find their true PTI
                }
                const groupMin = group.minPti !== undefined ? group.minPti : -100;
                const groupMax = group.maxPti !== undefined ? group.maxPti : 100;
                return pti >= groupMin && pti <= groupMax;
            });

            // Calculate Distance
            visibleGroups = visibleGroups.map(group => {
                let dist = null;
                let groupLat = group.locationLat;
                let groupLng = group.locationLng;

                // Backward compatibility lookup
                if (!groupLat && group.locationId && locationsMap[group.locationId]) {
                    groupLat = locationsMap[group.locationId].lat;
                    groupLng = locationsMap[group.locationId].lng;
                } else if (!groupLat && group.location && locationsByName[group.location]) {
                    groupLat = locationsByName[group.location].lat;
                    groupLng = locationsByName[group.location].lng;
                }

                if (homeClubData && homeClubData.lat && homeClubData.lng && groupLat && groupLng) {
                    dist = haversineDistance(homeClubData.lat, homeClubData.lng, groupLat, groupLng);
                }

                return { ...group, distance: dist };
            });

            // Sort by Distance (Ascending), then by Date
            visibleGroups.sort((a, b) => {
                if (a.distance !== null && b.distance !== null) {
                    return a.distance - b.distance;
                }
                // If one has distance and other doesn't, prioritize distance? 
                // Or push unknown distance to bottom? Let's push unknown to bottom.
                if (a.distance !== null) return -1;
                if (b.distance !== null) return 1;

                // Secondary sort: Date
                return (a.date || '').localeCompare(b.date || '');
            });

            setPublicGroups(visibleGroups);
        } catch (error) {
            console.error('Error fetching open huts:', error);
            toast.error('Failed to load huts');
        } finally {
            setLoading(false);
        }
    }, [existingGroupIds, filters]);

    // Initial Load
    useEffect(() => {
        const fetchUserData = async () => {
            if (user) {
                // 1. Fetch Profile for PTI
                let myPti = null;
                try {
                    const profileSnap = await getDoc(doc(db, 'userProfiles', user.uid));
                    if (profileSnap.exists()) {
                        myPti = profileSnap.data().pti;
                        setUserPti(myPti);
                    }
                } catch (e) {
                    console.error("Error fetching profile", e);
                }

                // 2. Fetch My Groups
                const myIds = await fetchUserGroups(user.uid);

                // 3. Fetch Open Huts
                fetchOpenHuts(user.uid, myPti, myIds);
            } else {
                setPublicGroups([]);
                setMyGroups([]);
                setLoading(false);
            }
        };

        fetchUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Handle Filter Change
    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        const newFilters = { ...filters, [name]: val };
        setFilters(newFilters);

        // Trigger fetch with new filters
        if (user) {
            fetchOpenHuts(user.uid, userPti, existingGroupIds, newFilters);
        }
    };

    const handleSliderChange = (val) => {
        const newFilters = { ...filters, minPti: val[0], maxPti: val[1] };
        setFilters(newFilters);

        if (user) {
            fetchOpenHuts(user.uid, userPti, existingGroupIds, newFilters);
        }
    };

    const clearFilters = () => {
        const defaultFilters = { minPti: -10, maxPti: 100, showAll: true };
        setFilters(defaultFilters);
        if (user) {
            fetchOpenHuts(user.uid, userPti, existingGroupIds, defaultFilters);
        }
    };

    const handleJoinHut = async (group) => {
        if (!user) return;

        // PTI Enforcement
        const groupMin = group.minPti !== undefined ? group.minPti : -100;
        const groupMax = group.maxPti !== undefined ? group.maxPti : 100;

        if (userPti !== null && userPti !== undefined) {
            if (userPti < groupMin || userPti > groupMax) {
                toast.error(`Your PTI (${userPti}) is outside the range for this hut (${groupMin}-${groupMax})`);
                return;
            }
        }

        const toastId = toast.loading('Joining hut...');

        try {
            const groupRef = doc(db, 'groups', group.id);

            const status = await runTransaction(db, async (transaction) => {
                const groupDoc = await transaction.get(groupRef);
                if (!groupDoc.exists()) throw new Error("Group does not exist!");

                const currentData = groupDoc.data();
                const currentCount = currentData.spotsTaken || 0;
                const maxPlayers = currentData.maxPlayers || 4;

                let newStatus = 'waitlist';
                if (currentCount < maxPlayers) {
                    newStatus = 'confirmed';
                    transaction.update(groupRef, { spotsTaken: currentCount + 1 });
                }

                // Add member
                const memberRef = doc(db, 'groups', group.id, 'members', user.uid);
                transaction.set(memberRef, {
                    userId: user.uid,
                    userName: user.displayName || user.email || 'Anonymous',
                    joinedAt: Timestamp.now(),
                    role: 'member',
                    status: newStatus
                });

                // Update user profile
                const userGroupRef = doc(db, 'userProfiles', user.uid, 'groups', group.id);
                transaction.set(userGroupRef, {
                    groupId: group.id,
                    groupName: currentData.name,
                    joinedAt: Timestamp.now(),
                    role: 'member'
                });

                return newStatus;
            });

            toast.success(`Joined with status: ${status.toUpperCase()}`, { id: toastId });
            // Refresh logic
            const myIds = await fetchUserGroups(user.uid);
            fetchOpenHuts(user.uid, userPti, myIds);

        } catch (error) {
            console.error("Join error", error);
            toast.error(`Failed to join: ${error.message}`, { id: toastId });
        }
    };

    const handleCreateHut = async (formData) => {
        if (!user) return;
        const toastId = toast.loading('Creating hut...');

        try {
            const userProfileRef = doc(db, 'userProfiles', user.uid);
            const userProfileSnap = await getDoc(userProfileRef);
            const userName = userProfileSnap.exists()
                ? userProfileSnap.data().name
                : user.displayName || user.email || 'Anonymous';

            const groupRef = doc(collection(db, 'groups'));
            const groupData = {
                ...formData,
                createdBy: user.uid,
                createdByName: userName,
                createdAt: Timestamp.now(),
                spotsTaken: 1 // Creator
            };

            await setDoc(groupRef, groupData);

            // Creator Member
            await setDoc(doc(db, 'groups', groupRef.id, 'members', user.uid), {
                userId: user.uid,
                userName: userName,
                joinedAt: Timestamp.now(),
                role: 'admin',
                status: 'confirmed'
            });

            // User Profile Link
            await setDoc(doc(db, 'userProfiles', user.uid, 'groups', groupRef.id), {
                groupId: groupRef.id,
                groupName: formData.name,
                joinedAt: Timestamp.now(),
                role: 'admin'
            });

            toast.success('Hut created!', { id: toastId });
            setShowCreateModal(false);
            navigate(`/huts/${groupRef.id}`);

        } catch (error) {
            console.error("Create error", error);
            toast.error(`Failed to create: ${error.message}`, { id: toastId });
        }
    };


    return (
        <div className="max-w-6xl mx-auto p-6">

            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-800 mb-2">Virtual Hut</h1>
                <p className="text-gray-600 mb-8">Find a game that matches your level, or start a new one.</p>

                <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-8 py-4 bg-sky-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:bg-sky-700 transition-all transform hover:-translate-y-1"
                >
                    🔥 Light a Fire (Create Hut)
                </button>
            </div>

            {user && <PendingInvites userId={user.uid} onInviteHandled={() => {
                fetchUserGroups(user.uid).then(ids => fetchOpenHuts(user.uid, userPti, ids));
            }} />}

            {/* My Games Section */}
            {user && myGroups.length > 0 && (
                <div className="mb-12">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">My Games</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {myGroups.map(group => (
                            <HutCard
                                key={group.id}
                                group={group}
                                onClick={(g) => navigate(`/huts/${g.id}`)}
                                highlight={true}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Filters Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-700">Filters</h3>
                    <div className="text-xs text-gray-400">
                        {publicGroups.length} huts found
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="flex-1 w-full max-w-sm">
                        <label className="block text-xs text-gray-500 mb-2">PTI Range ({filters.minPti} - {filters.maxPti})</label>
                        <div className="px-2">
                            <Slider
                                range
                                min={-20}
                                max={100}
                                step={1}
                                value={[filters.minPti, filters.maxPti]}
                                onChange={handleSliderChange}
                                trackStyle={[{ backgroundColor: '#0284c7' }]}
                                handleStyle={[
                                    { borderColor: '#0284c7', backgroundColor: '#fff' },
                                    { borderColor: '#0284c7', backgroundColor: '#fff' }
                                ]}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="showAll"
                                checked={filters.showAll}
                                onChange={handleFilterChange}
                                className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Show All (Ignore PTI)</span>
                        </label>

                        {(filters.minPti !== -10 || filters.maxPti !== 100 || filters.showAll !== false) && (
                            <button
                                onClick={clearFilters}
                                className="text-xs text-sky-600 hover:text-sky-800 underline"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-6">Open Huts</h2>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <LoadingSkeleton count={3} height="h-64" />
                </div>
            ) : publicGroups.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-500">No open huts found matching your criteria.</p>
                    <div className="mt-4 flex flex-col items-center gap-2">
                        <p className="text-sm text-gray-400">Try adjusting your filters or create a new one!</p>
                        {(filters.minPti !== -10 || filters.maxPti !== 100 || filters.showAll === false) && (
                            <button
                                onClick={clearFilters}
                                className="px-4 py-2 mt-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg shadow-sm hover:bg-gray-50"
                            >
                                Clear Active Filters
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {publicGroups.map(group => (
                        <HutCard
                            key={group.id}
                            group={group}
                            actionLabel="Join Game"
                            onAction={() => handleJoinHut(group)}
                            onClick={(g) => navigate(`/huts/${g.id}`)}
                        />
                    ))}
                </div>
            )}

            {showCreateModal && (
                <CreateHutModal
                    onClose={() => setShowCreateModal(false)}
                    onSubmit={handleCreateHut}
                />
            )}
        </div>
    );
}

export default HutsPage;
